const WGS84_A = 6378137;
const WGS84_F = 1 / 298.257223563;
const WGS84_E2 = WGS84_F * (2 - WGS84_F);

const radians = degrees => Number(degrees) * Math.PI / 180;
const degrees = value => Number(value) * 180 / Math.PI;

export function geodeticToEcef(latitude, longitude, height = 0) {
  const lat = radians(latitude), lon = radians(longitude), altitude = Number(height) || 0;
  const sinLat = Math.sin(lat), cosLat = Math.cos(lat), sinLon = Math.sin(lon), cosLon = Math.cos(lon);
  const primeVertical = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);
  return {
    x: (primeVertical + altitude) * cosLat * cosLon,
    y: (primeVertical + altitude) * cosLat * sinLon,
    z: (primeVertical * (1 - WGS84_E2) + altitude) * sinLat
  };
}

export function ecefToGeodetic(x, y, z) {
  const px = Number(x), py = Number(y), pz = Number(z);
  const longitude = Math.atan2(py, px), horizontal = Math.hypot(px, py);
  let latitude = Math.atan2(pz, horizontal * (1 - WGS84_E2));
  let height = 0;
  for (let iteration = 0; iteration < 8; iteration++) {
    const sinLat = Math.sin(latitude);
    const primeVertical = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);
    height = horizontal / Math.max(1e-12, Math.cos(latitude)) - primeVertical;
    latitude = Math.atan2(pz, horizontal * (1 - WGS84_E2 * primeVertical / (primeVertical + height)));
  }
  return { latitude: degrees(latitude), longitude: degrees(longitude), height };
}

export function createEnuFrame(latitude, longitude, height = 0) {
  const lat = radians(latitude), lon = radians(longitude);
  return {
    latitude: Number(latitude),
    longitude: Number(longitude),
    height: Number(height) || 0,
    origin: geodeticToEcef(latitude, longitude, height),
    sinLat: Math.sin(lat),
    cosLat: Math.cos(lat),
    sinLon: Math.sin(lon),
    cosLon: Math.cos(lon)
  };
}

export function ecefToEnu(point, frame) {
  const dx = Number(point.x) - frame.origin.x;
  const dy = Number(point.y) - frame.origin.y;
  const dz = Number(point.z) - frame.origin.z;
  return {
    east: -frame.sinLon * dx + frame.cosLon * dy,
    north: -frame.sinLat * frame.cosLon * dx - frame.sinLat * frame.sinLon * dy + frame.cosLat * dz,
    up: frame.cosLat * frame.cosLon * dx + frame.cosLat * frame.sinLon * dy + frame.sinLat * dz
  };
}

export function enuToEcef(point, frame) {
  const east = Number(point.east), north = Number(point.north), up = Number(point.up);
  return {
    x: frame.origin.x - frame.sinLon * east - frame.sinLat * frame.cosLon * north + frame.cosLat * frame.cosLon * up,
    y: frame.origin.y + frame.cosLon * east - frame.sinLat * frame.sinLon * north + frame.cosLat * frame.sinLon * up,
    z: frame.origin.z + frame.cosLat * north + frame.sinLat * up
  };
}

export function geodeticToEnu(latitude, longitude, height, frame) {
  return ecefToEnu(geodeticToEcef(latitude, longitude, height), frame);
}

export function enuToGeodetic(east, north, up, frame) {
  const ecef = enuToEcef({ east, north, up }, frame);
  return ecefToGeodetic(ecef.x, ecef.y, ecef.z);
}

export function worldReference(latitude, longitude, height = 0) {
  const ecef = geodeticToEcef(latitude, longitude, height);
  return {
    model: 'WGS84-ECEF',
    latitude: Number(latitude),
    longitude: Number(longitude),
    height: Number(height) || 0,
    ecef
  };
}

export const WGS84 = Object.freeze({ semiMajorAxis: WGS84_A, flattening: WGS84_F, eccentricitySquared: WGS84_E2 });
